// An order's progress. On the property page, the real steps our team's checks move it through
// (Neil, 10 Oct 2026): Ordered → Roof checked for existing solar panels → Researching the property →
// Research checked → Report ready, with what's happening now under the current step. In the My
// properties list (compact), three steps that fit a row: Ordered → In progress → Report ready.
// A failed order shows instead as refunded.
import { Check, XCircle } from "lucide-react";
import type { AboutStatus, PackStatus } from "../api";

interface Step {
  label: string;
  /** What's happening while this is the current step */
  now: string;
}

const STEPS: Step[] = [
  { label: "Ordered", now: "Your order is in." },
  { label: "Roof checked", now: "Our team is checking the roof for existing solar panels." },
  { label: "Researching", now: "We're researching the property: who owns and runs it, its plans and funding." },
  { label: "Research checked", now: "Our team is checking the research." },
  { label: "Report ready", now: "Your Solar Opportunity Report is ready: export or share it from the panel on the right." },
];
const COMPACT = ["Ordered", "In progress", "Report ready"];

/** Which step the order is on (an index into STEPS). */
function stepOf(status: PackStatus, about?: AboutStatus | null): number {
  if (status === "completed") return 4;
  if (status === "ordered") return 1; // the roof check is under way
  // In progress: the roof has passed; the research is running, then with our team
  if (about === "checking" || about === "approved") return 3;
  if (about === "unavailable") return 3; // research rejected: our team decides how to finish
  return 2;
}

export function PackProgress({ status, about, compact = false }: { status: PackStatus; about?: AboutStatus | null; compact?: boolean }) {
  if (status === "failed") {
    return (
      <span className="pack-failed">
        <XCircle size={14} aria-hidden="true" /> Couldn't be prepared · credits refunded
      </span>
    );
  }

  if (compact) {
    const reached = status === "completed" ? 2 : status === "in-progress" ? 1 : 0;
    return (
      <ol className="pack-progress is-compact" aria-label={`Pack: ${COMPACT[reached]}`}>
        {COMPACT.map((label, i) => (
          <li key={label} className={i < reached ? "is-done" : i === reached ? "is-current" : ""} aria-current={i === reached ? "step" : undefined}>
            <span className="pack-dot">{i < reached || status === "completed" ? <Check size={13} aria-hidden="true" /> : null}</span>
            <span className="pack-label">{label}</span>
          </li>
        ))}
      </ol>
    );
  }

  const reached = stepOf(status, about);
  const done = status === "completed";
  return (
    <>
      <ol className="pack-progress is-steps" aria-label={`Order: ${STEPS[reached].label}`}>
        {STEPS.map((s, i) => (
          <li key={s.label} className={i < reached ? "is-done" : i === reached ? "is-current" : ""} aria-current={i === reached ? "step" : undefined}>
            <span className="pack-dot">{i < reached || done ? <Check size={15} aria-hidden="true" /> : null}</span>
            <span className="pack-label">{s.label}</span>
          </li>
        ))}
      </ol>
    </>
  );
}
